import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@travelnest/hui';

import { coverOverlap, coverPhoto } from '@/components/layout/CoverLayout';
import { Stack } from '@/components/ui/Stack';
import { DialogDrawer } from '@/components/ui/dialog-drawer';
import { colors, font, mq, mqMax, radii, spacing } from '@/lib/theme';
import { useI18n } from '@/locales';
import type { IImage } from '@/types/content';

import { PropertyImage } from './PropertyImage';

const Frame = styled.section`
	position: relative;
	border-radius: ${radii.radii40};
	overflow: hidden;
	background: ${colors.LM_NU20};
	${coverPhoto}
`;

/* Phones: one image at a time, swipe to move. */
const Strip = styled.div`
	display: flex;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	scroll-behavior: smooth;
	scrollbar-width: none;
	aspect-ratio: 4 / 3;

	&::-webkit-scrollbar {
		display: none;
	}

	${mq.md} {
		display: none;
	}
`;

const Slide = styled.div`
	flex: 0 0 100%;
	scroll-snap-align: center;
	scroll-snap-stop: always;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

/* Tablets and up: one hero plus a grid of up to four. */
const Mosaic = styled.div<{ tiles: number }>`
	display: none;

	${mq.md} {
		display: grid;
		gap: ${spacing.space20};
		aspect-ratio: 2 / 1;
		grid-template-columns: ${({ tiles }) => (tiles > 1 ? '1fr 1fr' : '1fr')};
		grid-template-rows: 1fr 1fr;

		> :first-of-type {
			grid-row: span 2;
		}
	}

	${mq.lg} {
		grid-template-columns: ${({ tiles }) =>
			tiles > 3 ? '2fr 1fr 1fr' : tiles > 1 ? '2fr 1fr' : '1fr'};
	}
`;

const Tile = styled.button`
	position: relative;
	padding: 0;
	border: 0;
	background: ${colors.LM_NU20};
	cursor: zoom-in;
	overflow: hidden;
	min-height: 0;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 300ms ease-out;
	}
	&:hover img {
		transform: scale(1.03);
	}
	&:focus-visible {
		outline: 3px solid ${colors.BB80};
		outline-offset: -3px;
	}
`;

const Counter = styled.div`
	position: absolute;
	right: ${spacing.space40};
	/* Clear of the page's panel, which rises over the photo's foot. */
	bottom: calc(${coverOverlap} + ${spacing.space30});
	padding: 2px ${spacing.space20};
	border-radius: ${radii.radii20};
	background: rgba(17, 35, 58, 0.72);
	color: ${colors.LM_NU0};
	${font('IM80')}
	font-variant-numeric: tabular-nums;

	${mq.md} {
		display: none;
	}
`;

const ShowAll = styled.div`
	position: absolute;
	right: ${spacing.space40};
	bottom: ${spacing.space40};

	${mqMax.md} {
		display: none;
	}
`;

const LightboxList = styled.div`
	display: grid;
	gap: ${spacing.space30};

	img {
		width: 100%;
		border-radius: ${radii.radii30};
	}
`;

const Caption = styled.p`
	${font('IR90')}
	color: ${colors.typography.body};
	margin: ${spacing.space10} 0 0;
`;

interface GalleryProps {
	images: IImage[];
	propertyName: string;
}

/**
 * The photos at the top of the property page: a swipeable strip on phones,
 * a mosaic on larger screens, and a lightbox with every photo.
 */
export const Gallery = ({ images, propertyName }: GalleryProps) => {
	const t = useI18n();
	const [index, setIndex] = useState(0);
	const [lightbox, setLightbox] = useState<number | null>(null);
	const stripRef = useRef<HTMLDivElement>(null);

	// Track which slide is in view so the counter follows the swipe.
	useEffect(() => {
		const strip = stripRef.current;
		if (!strip) return;
		const onScroll = () => {
			setIndex(Math.round(strip.scrollLeft / strip.clientWidth));
		};
		strip.addEventListener('scroll', onScroll, { passive: true });
		return () => strip.removeEventListener('scroll', onScroll);
	}, []);

	const open = useCallback((at: number) => setLightbox(at), []);
	const close = useCallback(() => setLightbox(null), []);

	const tiles = images.slice(0, 5);
	const altFor = (image: IImage, i: number) =>
		image.caption ||
		`${t('property.gallery.photoOf')} ${propertyName} (${i + 1})`;

	if (images.length === 0) {
		return null;
	}

	return (
		<Frame aria-label={t('property.gallery.title')}>
			<Strip ref={stripRef}>
				{images.map((image, i) => (
					<Slide key={image.id} onClick={() => open(i)}>
						<PropertyImage
							image={image}
							alt={altFor(image, i)}
							sizes="100vw"
							loading={i === 0 ? 'eager' : 'lazy'}
							fetchPriority={i === 0 ? 'high' : undefined}
						/>
					</Slide>
				))}
			</Strip>
			<Counter aria-live="polite">
				{index + 1} / {images.length}
			</Counter>

			<Mosaic tiles={tiles.length}>
				{tiles.map((image, i) => (
					<Tile
						key={image.id}
						type="button"
						onClick={() => open(i)}
						aria-label={altFor(image, i)}
					>
						<PropertyImage
							image={image}
							alt=""
							sizes={
								i === 0
									? '(min-width: 1200px) 640px, 50vw'
									: '(min-width: 1200px) 320px, 25vw'
							}
							loading={i === 0 ? 'eager' : 'lazy'}
							fetchPriority={i === 0 ? 'high' : undefined}
						/>
					</Tile>
				))}
			</Mosaic>
			{images.length > 1 && (
				<ShowAll>
					<Button
						text={t('property.gallery.showAll')}
						variant="white"
						size="small"
						icon="Photo"
						onClick={() => open(0)}
					/>
				</ShowAll>
			)}

			<DialogDrawer
				open={lightbox !== null}
				onOpenChange={(next) => !next && close()}
				title={`${t('property.gallery.title')} · ${images.length}`}
				closeLabel={t('common.close')}
				desktopWidth="880px"
				mobileFill="full"
			>
				<LightboxList>
					{images.map((image, i) => (
						<Stack key={image.id} gap="space10">
							<PropertyImage
								image={image}
								alt={altFor(image, i)}
								sizes="(min-width: 768px) 720px, 100vw"
								loading={i < 3 ? 'eager' : 'lazy'}
							/>
							{image.caption && (
								<Caption>{image.caption}</Caption>
							)}
						</Stack>
					))}
				</LightboxList>
			</DialogDrawer>
		</Frame>
	);
};
